import { useMemo } from "react";
import { useEventTitles } from "./useEventTitles";
import { compoundEventKey } from "./providers/types";

/**
 * Decorate an RBC event array (as produced by buildAvailabilityCalendarEvents
 * or its scheduler-page equivalents) with titles resolved client-side by the
 * enrichment hook.
 *
 * Enrichment runs for any event whose `resource` carries a structural
 * identifier triple ({ provider, calendarId, eventId }). Kinds without those
 * fields (finalizedSession, marks, etc.) keep their existing title.
 *
 * The `placeholderTitle` callback is invoked for any external event whose
 * title hasn't resolved yet (or whose enrichment failed). Default: empty
 * string — RBC will render time-only. Pages can pass a function returning
 * something like "Busy" or a localized "(loading)" if they prefer.
 *
 * @param {Array<{
 *   id: string,
 *   title: string,
 *   start: Date,
 *   end: Date,
 *   allDay: boolean,
 *   resource: {
 *     kind: string,
 *     provider?: string,
 *     calendarId?: string,
 *     eventId?: string,
 *     [k: string]: unknown,
 *   },
 * }>} events
 * @param {{
 *   placeholderTitle?: (evt: object) => string,
 *   notFoundTitle?: (evt: object) => string,
 * }} [opts]
 */
export function useEnrichedCalendarEvents(events, opts = {}) {
  const { placeholderTitle, notFoundTitle } = opts;

  const requests = useMemo(() => {
    if (!Array.isArray(events)) return [];
    const out = [];
    const seen = new Set();
    for (const evt of events) {
      const r = evt?.resource;
      if (!r || !r.provider || !r.calendarId || !r.eventId) continue;
      const key = compoundEventKey(r.provider, r.calendarId, r.eventId);
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({ provider: r.provider, calendarId: r.calendarId, eventId: r.eventId });
    }
    return out;
  }, [events]);

  const titles = useEventTitles(requests);

  return useMemo(() => {
    if (!Array.isArray(events)) return events;
    return events.map((evt) => {
      const r = evt?.resource;
      if (!r || !r.provider || !r.calendarId || !r.eventId) return evt;
      const result = titles.get(compoundEventKey(r.provider, r.calendarId, r.eventId));
      if (result?.title) {
        return { ...evt, title: result.title };
      }
      if (result?.error === "not_found") {
        return {
          ...evt,
          title: notFoundTitle ? notFoundTitle(evt) : evt.title || "",
        };
      }
      return {
        ...evt,
        title: placeholderTitle ? placeholderTitle(evt) : evt.title || "",
      };
    });
  }, [events, titles, placeholderTitle, notFoundTitle]);
}
